import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { User, LogOut, Menu, X, Sparkles, CreditCard, Shield, Bell, BellOff, Download } from 'lucide-react';

function DesktopNav({ navLinks, isActive }) {
  return (
    <nav className="hidden md:flex items-center gap-8">
      {navLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className={`text-sm font-medium transition-colors flex items-center gap-1.5 ${
            isActive(link.href) ? 'text-[#292927]' : 'text-[#63635E] hover:text-[#292927]'
          }`}
          data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {link.icon && <link.icon className="w-4 h-4" strokeWidth={1.5} />}
          {link.label}
          {link.primary && <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-[#4A5D4E] text-white rounded-full font-bold leading-none">AI</span>}
        </Link>
      ))}
    </nav>
  );
}

function UserActions({ user, logout, installPrompt, onInstall, supported, permission, subscribe }) {
  return (
    <div className="hidden md:flex items-center gap-3">
      {installPrompt && (
        <button onClick={onInstall} className="flex items-center gap-1.5 text-xs font-medium text-[#4A5D4E] bg-[#4A5D4E]/10 hover:bg-[#4A5D4E]/20 px-3 py-1.5 rounded-full transition-colors" data-testid="install-pwa-btn">
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Instalar App
        </button>
      )}
      {user && user !== false ? (
        <div className="flex items-center gap-3">
          {supported && (
            <button
              onClick={subscribe}
              className={`p-2 rounded-full transition-colors ${permission === 'granted' ? 'text-[#4A5D4E] bg-[#4A5D4E]/10' : 'text-[#A0A09A] hover:text-[#4A5D4E] hover:bg-[#4A5D4E]/10'}`}
              title={permission === 'granted' ? 'Notificações ativadas' : 'Ativar notificações'}
              data-testid="notifications-btn"
            >
              {permission === 'granted' ? <Bell className="w-4 h-4" strokeWidth={1.5} /> : <BellOff className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          )}
          <Link to="/dashboard" className="flex items-center gap-2 text-sm font-medium text-[#63635E] hover:text-[#292927] transition-colors" data-testid="dashboard-link">
            <User className="w-4 h-4" strokeWidth={1.5} /> {user.name || 'Minha Conta'}
          </Link>
          <button onClick={logout} className="flex items-center gap-2 text-sm font-medium text-[#63635E] hover:text-[#292927] transition-colors" data-testid="logout-button">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sair
          </button>
        </div>
      ) : (
        <>
          <Link to="/login" className="text-sm font-medium text-[#63635E] hover:text-[#292927] transition-colors" data-testid="login-link">Entrar</Link>
          <Link to="/register" className="btn-primary text-sm py-2.5 px-6" data-testid="register-link">Começar</Link>
        </>
      )}
    </div>
  );
}

function MobileMenu({ navLinks, user, logout, isActive, onClose }) {
  return (
    <div className="md:hidden py-4 border-t border-[#EAE8E3] animate-fade-in">
      <nav className="flex flex-col gap-4">
        {navLinks.map((link) => (
          <Link key={link.href} to={link.href} onClick={onClose}
            className={`text-base font-medium flex items-center gap-2 ${isActive(link.href) ? 'text-[#292927]' : 'text-[#63635E]'}`}>
            {link.icon && <link.icon className="w-4 h-4" strokeWidth={1.5} />} {link.label}
          </Link>
        ))}
        <div className="pt-4 border-t border-[#EAE8E3] flex flex-col gap-4">
          {user && user !== false ? (
            <>
              <Link to="/dashboard" onClick={onClose} className="text-base font-medium text-[#63635E] flex items-center gap-2">
                <User className="w-4 h-4" strokeWidth={1.5} /> Minha Conta
              </Link>
              <button onClick={() => { logout(); onClose(); }} className="text-base font-medium text-[#63635E] flex items-center gap-2">
                <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sair
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={onClose} className="text-base font-medium text-[#63635E]">Entrar</Link>
              <Link to="/register" onClick={onClose} className="btn-primary text-center">Começar</Link>
            </>
          )}
        </div>
      </nav>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const { permission, supported, subscribe } = usePushNotifications();

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => { if (!installPrompt) return; installPrompt.prompt(); setInstallPrompt(null); };
  const isAdmin = user?.role === 'admin';
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { href: '/match', label: 'Match IA', icon: Sparkles, primary: true },
    { href: '/payments', label: 'Assistência', icon: CreditCard },
    { href: '/professionals', label: 'Profissionais' },
    ...(isAdmin ? [{ href: '/admin', label: 'Admin', icon: Shield }] : [])
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/80 border-b border-[#EAE8E3]" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="/betherapy-logo.png" alt="Betherapy" className="h-14 sm:h-16 w-auto" />
          </Link>
          <DesktopNav navLinks={navLinks} isActive={isActive} />
          <UserActions user={user} logout={logout} installPrompt={installPrompt} onInstall={handleInstall} supported={supported} permission={permission} subscribe={subscribe} />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-[#63635E] hover:text-[#292927]" data-testid="mobile-menu-button">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && <MobileMenu navLinks={navLinks} user={user} logout={logout} isActive={isActive} onClose={() => setMobileMenuOpen(false)} />}
      </div>
    </header>
  );
}
